import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Search from "@insite/mobius/Icons/Search";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SearchIcon from "@tCentro/Icons/SearchIcon";
import CustomApplicationState from "@tCentro/Store/CustomApplicationState";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState & CustomApplicationState) => {
    const brandCategoriesData = state.t_components.CustomBrandData.brandCategoriesData;
    return {
        productFilters: state.pages.productList.productFilters,
        hideSearchWithin: getSettingsCollection(state).searchSettings.searchProvider === "Commerce Search v3",
        brandCategoriesData,
    };
};

const mapDispatchToProps = {
    addProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface NISProductCategoriesSearchWithinStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    searchTextField?: TextFieldPresentationProps;
}

export const searchWithinStyles: NISProductCategoriesSearchWithinStyles = {
    titleText: {
        variant: "h6",
    },
    searchTextField: {
        iconProps: { src: SearchIcon },
        cssOverrides: {
            formField: css`
                width: 100%;

                margin-top: 5px;
                div {
                    input {
                        border-radius: 40px !important;
                        padding: 10px 16px !important;
                        padding-right: 32px !important;
                        height: 48px;
                        background: ${COLORS.background.base} !important;
                        color: ${COLORS.black} !important;
                        font-size: ${TYPOGRAPHY.size.smaller} !important;
                        font-weight: ${TYPOGRAPHY.weight.base};
                    }
                    span {
                        top: 15px !important;
                        right: 15px !important;
                        width: 18px !important;
                        height: 18px !important;
                        padding: 0px !important;
                        svg {
                            width: 18px;
                            height: 18px;
                        }
                    }
                }
            `,
        },
    },
};

const styles = searchWithinStyles;

const NISProductCategoriesSearchWithin: FC<Props> = ({
    addProductFilters,
    productFilters,
    hideSearchWithin,
    brandCategoriesData,
}) => {
    const [searchText, setSearchText] = useState("");

    const onChangeSearchText = (event: React.ChangeEvent<HTMLInputElement>) => {
        const filteredText = event.currentTarget.value.replace(/[^a-zA-Z0-9 ]/g, "");
        const filteredArray = brandCategoriesData.filter(category =>
            category.categoryName.toLowerCase().includes(filteredText.toLowerCase()),
        );
        console.log("filteredArray", filteredArray);
        setSearchText(event.currentTarget.value);
    }

    const doSearch = () => {
        if (searchText.trim() !== "") {
            const uniqueSearchWithinQueries = searchText
                .split(" ")
                .filter((value, index, array) => array.indexOf(value) === index);
                console.log("uniqueSearchWithinQueries", uniqueSearchWithinQueries);

            // addProductFilters({ searchWithinQueries: uniqueSearchWithinQueries });
        }
        setSearchText("");
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            doSearch();
        } else {
            setSearchText(event.currentTarget.value);
        }
    };

    let pageType: string;
    if (productFilters.pageProductLineId) {
        pageType = "Product Line";
    } else if (productFilters.pageBrandId) {
        pageType = "Brand";
    } else if (productFilters.pageCategoryId) {
        pageType = "Category";
    } else {
        pageType = "Results";
    }

    const label = translate(`Search Within ${pageType}`);

    if (hideSearchWithin) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <TextField
                {...styles.searchTextField}
                iconClickableProps={{ onClick: doSearch, "aria-label": `Search within ${pageType}` }}
                value={searchText}
                onChange={event => onChangeSearchText(event)}
                onKeyDown={handleKeyDown}
                placeholder={translate("Search Product Categories")}
                type="text"
                data-test-selector="NISProductCategoriesSearchWithin"
                id="productsKeywordSearch"
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(NISProductCategoriesSearchWithin),
    definition: {
        group: "Basic",
        displayName: "Product Categories Search Within",
    },
};

export default widgetModule;
